import React, { useRef, type PropsWithChildren } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Box, Inline, xcss, Text } from '@atlaskit/primitives';
import RepeatIcon from '@atlaskit/icon-lab/core/repeat';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { ColoredDueDate } from '@atlassian/jira-coloured-due-date/src/ui/index.tsx';
import { DateWithUrgencyIndicator } from '@atlassian/jira-date-with-urgency-indicator/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import { useDateTimeFormatter } from '@atlassian/jira-issue-format-date/src/main.tsx';
import { DUE_DATE } from '@atlassian/jira-issue-view-configurations/src/index.tsx';
import { useRecurWorkItemState } from '@atlassian/jira-recur-work-item/src/controllers/index.tsx';
import { isRecurOnCompleteToggleRule } from '@atlassian/jira-recur-work-item/src/ui/utils.tsx';
import { getNoOfDaysSinceUTCDateString } from '@atlassian/jira-issue-view-common/src/utils/date-utils/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import type { date_issueFieldDateReadviewFull_DateReadView$key as DateFragment } from '@atlassian/jira-relay/src/__generated__/date_issueFieldDateReadviewFull_DateReadView.graphql';
import { HoverPopoverWithContext } from '@atlassian/jira-hover-popover/src/ui/popover-with-context/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const RecurringWorkItemIcon = ({ useUrgentColour }: { useUrgentColour: boolean }) => {
	const { issueProperties, isUpdatingRule } = useRecurWorkItemState();

	if ((!issueProperties && !isUpdatingRule) || isRecurOnCompleteToggleRule(issueProperties)) {
		return null;
	}

	return (
		<RepeatIcon
			label=""
			testId="issue-field-date-readview-full.ui.date.recur-work-icon"
			{...(useUrgentColour ? { color: token('color.icon.danger') } : {})}
		/>
	);
};

const EmptyContainer = ({ children }: PropsWithChildren<unknown>) => {
	return <>{children}</>;
};

export const DateReadView = ({
	fragmentRef,
	fieldId,
	includeUrgencyTreatment,
	shouldShowRecurWorkIcon = false,
	shouldShowHoverPopover,
}: Props) => {
	const { formatDateOnly } = useDateTimeFormatter();
	const { formatMessage } = useIntl();
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();
	const noValueTextExperiment = formatMessage(
		fieldId === DUE_DATE ? messages.noValueTextExperimentDueDate : messages.noValueTextExperiment,
	);
	const data = useFragment<DateFragment>(
		graphql`
			fragment date_issueFieldDateReadviewFull_DateReadView on JiraDatePickerField
			@argumentDefinitions(
				isSingleLineEventDelegatorEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-single-line-event-delegator-enabled.relayprovider"
				}
			) {
				id @include(if: $isSingleLineEventDelegatorEnabled)
				date
			}
		`,
		fragmentRef,
	);

	const { date } = data || {};

	const contentRef = useRef(null);

	const hoverPopoverRefFactory =
		shouldShowHoverPopover && fg('issue_table_single_line_row_height_event_delegator')
			? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
				useHoverPopoverContentRef(data.id)
			: null;

	let hoverPopoverRef: HoverPopoverContentRef = null;

	if (!date) {
		return <EmptyFieldText emptyText={isExperimentEnabled ? noValueTextExperiment : undefined} />;
	}

	const daysSinceDate = getNoOfDaysSinceUTCDateString(date);
	const isDatePast = daysSinceDate != null && daysSinceDate > 0;
	const isDueToday = daysSinceDate === 0;

	const renderColoredDueDate = () => (
		<ColoredDueDate
			dateString={date}
			pastDue={isDatePast && Boolean(includeUrgencyTreatment) && fieldId === DUE_DATE}
			dueToday={isDueToday && Boolean(includeUrgencyTreatment) && fieldId === DUE_DATE}
			warningIconTestId="date-field.warning-icon"
			clockIconTestId="date-field.clock-icon"
			calendarIconTestId="date-field.calendar-icon"
			maybeShowRecurIcon={shouldShowRecurWorkIcon}
			recurIconTestId="date-field.recur-icon"
		/>
	);

	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: formatDateOnly(date),
		});
	}

	const renderedValue =
		includeUrgencyTreatment &&
		fieldId === DUE_DATE &&
		expVal('due_date_colured_field', 'showUrgencyIndicator', false) ? (
			<DateWithUrgencyIndicator value={date} />
		) : (
			formatDateOnly(date)
		);

	const Container = shouldShowRecurWorkIcon ? Inline : EmptyContainer;

	/**
	 * Hover popover is only enabled on NIN, GNIN, and CHIN and hence the conditional rendering of
	 * other formatted dates is ignroed
	 */
	if (shouldShowHoverPopover) {
		return fg('issue_table_single_line_row_height_event_delegator') ? (
			<Container
				space="space.075"
				alignBlock="center"
				testId="issue-field-date-readview-full.ui.date.container"
			>
				<Box as="span" xcss={[dateReadViewStyles, singleLineStyles]} ref={hoverPopoverRef}>
					{formatDateOnly(date)}
				</Box>
			</Container>
		) : (
			<HoverPopoverWithContext contentRef={contentRef} content={formatDateOnly(date)}>
				{({ ref }) => (
					<Container
						space="space.075"
						alignBlock="center"
						testId="issue-field-date-readview-full.ui.date.container"
					>
						<Box as="span" xcss={[dateReadViewStyles]}>
							<Text maxLines={1} ref={mergeRefs(ref, contentRef)}>
								{formatDateOnly(date)}
							</Text>
						</Box>
					</Container>
				)}
			</HoverPopoverWithContext>
		);
	}

	return (
		<Container
			space="space.075"
			alignBlock="center"
			testId="issue-field-date-readview-full.ui.date.container"
		>
			<Box
				as="span"
				xcss={[
					dateReadViewStyles,
					!(
						includeUrgencyTreatment &&
						fieldId === DUE_DATE &&
						expVal('color_due_date_all_views', 'showColoredDueDate', false)
					) && clampStyles,
				]}
			>
				{includeUrgencyTreatment &&
				fieldId === DUE_DATE &&
				expVal('color_due_date_all_views', 'showColoredDueDate', false)
					? renderColoredDueDate()
					: renderedValue}
			</Box>
			{!expVal('color_due_date_all_views', 'showColoredDueDate', false) &&
				shouldShowRecurWorkIcon && (
					<RecurringWorkItemIcon
						useUrgentColour={
							(isDatePast || isDueToday) &&
							!!includeUrgencyTreatment &&
							expVal('due_date_colured_field', 'showUrgencyIndicator', false)
						}
					/>
				)}
		</Container>
	);
};

const dateReadViewStyles = xcss({
	display: '-webkit-box',
	maxWidth: '100%',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const clampStyles = xcss({
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 2,
});

const singleLineStyles = xcss({
	WebkitBoxOrient: 'vertical',
	WebkitLineClamp: 1,
});
